import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { usePaywall } from "@shared/hooks/use-paywall";

type LegalLanguage = "en" | "ar" | "de" | "es" | "fr";
type LegalPageKind = "privacy" | "terms";

interface LegalSectionCopy {
  id: string;
  title: string;
  paragraphs: string[];
  items?: string[];
}

type LegalCopy = Record<
  LegalLanguage,
  Record<LegalPageKind, LegalSectionCopy[]>
>;

const SUPPORTED_LEGAL_LANGUAGES: LegalLanguage[] = ["en", "ar", "de", "es", "fr"];

const LEGAL_COPY: LegalCopy = {
  en: {
    privacy: [
      {
        id: "introduction",
        title: "Introduction",
        paragraphs: [
          "ProperHumanDiet is a health tracking platform for dietary protocols, symptoms, lifestyle logs, knowledge resources, community support, and paid AI tools.",
          "This policy explains how we handle information you provide while using the website, dashboard, community, wallet, and AI-assisted features. ProperHumanDiet is not a medical provider.",
        ],
      },
      {
        id: "data-we-collect",
        title: "Information we collect",
        paragraphs: [
          "We collect only the information needed to operate the service, protect accounts, personalize tracking, and provide requested AI features.",
        ],
        items: [
          "Account and profile details such as name, email, language, timezone, country, biography, and preferences.",
          "Health tracking entries such as symptoms, food logs, supplements, medications, lab results, visits, photos, journals, and protocol progress.",
          "Community content such as posts, recipes, comments, reactions, reports, mentions, and messages you choose to submit.",
          "Payment records such as wallet balance, credit ledger entries, checkout status, refunds, and transaction identifiers.",
          "Security and usage data such as device metadata, session records, request logs, and error diagnostics.",
        ],
      },
      {
        id: "ai-and-credits",
        title: "AI features and credits",
        paragraphs: [
          "When you use AI features, we process the prompt, uploaded file, selected language, relevant health context, and usage metadata needed to answer the request, estimate cost, debit or refund credits, and create audit records.",
          "Features may include the health assistant, meal photo analysis, symptom photo analysis, lab interpretation, visit note summaries, transcript enhancement, meal planning, community personas, and other AI surfaces exposed in the app. System-wallet-routed features do not debit your personal wallet.",
        ],
      },
      {
        id: "sharing",
        title: "How information is shared",
        paragraphs: [
          "We do not sell personal health information. We share data only when needed to provide the service, comply with law, prevent abuse, or process a user-requested action.",
        ],
        items: [
          "Infrastructure, email, analytics, monitoring, payment, and AI service providers receive only the data needed for their role.",
          "Community content may be visible to other users according to the surface where you post it.",
          "Legal, safety, fraud-prevention, or security requests may require limited disclosure.",
        ],
      },
      {
        id: "choices-security",
        title: "Your choices and security",
        paragraphs: [
          "You can update profile settings, language, timezone, units, dietary preferences, and notification choices in the app. Where available, you can request exports or account deletion.",
          "We use access controls, encryption in transit, audit logs, and operational safeguards. No online service can be guaranteed perfectly secure, and urgent medical issues should be handled through qualified clinicians or emergency services.",
        ],
      },
      {
        id: "contact",
        title: "Contact",
        paragraphs: [
          "For privacy questions, data requests, or safety concerns, contact support@theproperhumandiet.net.",
        ],
      },
    ],
    terms: [
      {
        id: "acceptance",
        title: "Acceptance of terms",
        paragraphs: [
          "By accessing or using ProperHumanDiet, you agree to these Terms. If you do not agree, do not use the service.",
          "You must be legally able to use the service and responsible for the information you submit.",
        ],
      },
      {
        id: "service",
        title: "The service",
        paragraphs: [
          "ProperHumanDiet provides tools for tracking diet protocols, symptoms, supplements, medications, labs, lifestyle habits, progress, knowledge resources, community support, and paid AI-assisted workflows.",
        ],
      },
      {
        id: "medical-disclaimer",
        title: "Medical disclaimer",
        paragraphs: [
          "ProperHumanDiet provides educational and tracking tools only. It does not diagnose, treat, cure, prevent disease, replace a clinician, or provide emergency care.",
          "Always consult qualified healthcare professionals before changing medication, supplements, diet, or treatment plans.",
        ],
      },
      {
        id: "accounts-content",
        title: "Accounts and user content",
        paragraphs: [
          "You are responsible for protecting your credentials, keeping account information accurate, and making sure you have the right to upload or share any content you submit.",
          "You retain ownership of your submitted content. You grant ProperHumanDiet the permission needed to store, display, process, translate, summarize, analyze, and transmit it only to operate the service and features you request.",
        ],
      },
      {
        id: "credits-ai",
        title: "Credits, wallet, and AI use",
        paragraphs: [
          "Paid AI features use one-time credit packs and wallet balances. The app should show a cost estimate or credit indicator before paid calls where applicable.",
          "Successful AI calls may debit credits and create usage records. Provider failures should fail gracefully and refund credits when a debit occurred without a completed result. Insufficient balance requires topping up before continuing.",
        ],
      },
      {
        id: "community",
        title: "Community conduct",
        paragraphs: [
          "Use community features respectfully. Do not harass others, post dangerous medical instructions, impersonate people, spam, violate privacy, or upload content you do not have permission to share.",
          "We may moderate, remove, restrict, or report content or accounts that create safety, legal, privacy, or abuse risks.",
        ],
      },
      {
        id: "contact",
        title: "Contact",
        paragraphs: [
          "For questions about these Terms, contact support@theproperhumandiet.net.",
        ],
      },
    ],
  },
  ar: {
    privacy: [
      {
        id: "introduction",
        title: "مقدمة",
        paragraphs: [
          "ProperHumanDiet منصة لتتبع الصحة تدعم البروتوكولات الغذائية، الأعراض، سجلات نمط الحياة، مصادر المعرفة، المجتمع، وأدوات الذكاء الاصطناعي المدفوعة.",
          "توضح هذه السياسة كيف نتعامل مع المعلومات التي تقدمها أثناء استخدام الموقع ولوحة التحكم والمجتمع والمحفظة والميزات المدعومة بالذكاء الاصطناعي. ProperHumanDiet ليست جهة طبية.",
        ],
      },
      {
        id: "data-we-collect",
        title: "المعلومات التي نجمعها",
        paragraphs: [
          "نجمع فقط المعلومات اللازمة لتشغيل الخدمة، حماية الحسابات، تخصيص التتبع، وتقديم ميزات الذكاء الاصطناعي التي تطلبها.",
        ],
        items: [
          "بيانات الحساب والملف الشخصي مثل الاسم، البريد الإلكتروني، اللغة، المنطقة الزمنية، البلد، السيرة الذاتية، والتفضيلات.",
          "إدخالات التتبع الصحي مثل الأعراض، الطعام، المكملات، الأدوية، نتائج المختبر، الزيارات، الصور، اليوميات، وتقدم البروتوكولات.",
          "محتوى المجتمع مثل المنشورات، الوصفات، التعليقات، التفاعلات، البلاغات، الإشارات، والرسائل التي تختار إرسالها.",
          "سجلات الدفع مثل رصيد المحفظة، دفتر الأرصدة، حالة الدفع، المبالغ المستردة، ومعرّفات المعاملات.",
          "بيانات الأمان والاستخدام مثل بيانات الجهاز، الجلسات، سجلات الطلبات، وتشخيص الأخطاء.",
        ],
      },
      {
        id: "ai-and-credits",
        title: "ميزات الذكاء الاصطناعي والأرصدة",
        paragraphs: [
          "عند استخدام ميزات الذكاء الاصطناعي، نعالج الطلب، الملف المرفوع، اللغة المختارة، السياق الصحي ذي الصلة، وبيانات الاستخدام اللازمة للإجابة، تقدير التكلفة، خصم الأرصدة أو ردها، وإنشاء سجلات تدقيق.",
          "قد تشمل الميزات المساعد الصحي، تحليل صور الوجبات، تحليل صور الأعراض، تفسير المختبر، تلخيص زيارات الطبيب، تحسين النصوص، تخطيط الوجبات، شخصيات المجتمع، وأي سطح ذكاء اصطناعي آخر يظهر في التطبيق. الميزات الموجهة عبر محفظة النظام لا تخصم من محفظتك الشخصية.",
        ],
      },
      {
        id: "sharing",
        title: "كيفية مشاركة المعلومات",
        paragraphs: [
          "لا نبيع المعلومات الصحية الشخصية. نشارك البيانات فقط عند الحاجة لتقديم الخدمة، الامتثال للقانون، منع إساءة الاستخدام، أو تنفيذ إجراء طلبه المستخدم.",
        ],
        items: [
          "مزودو البنية التحتية والبريد والتحليلات والمراقبة والدفع والذكاء الاصطناعي يتلقون فقط البيانات اللازمة لدورهم.",
          "قد يكون محتوى المجتمع مرئياً لمستخدمين آخرين حسب المكان الذي تنشر فيه.",
          "قد تتطلب الطلبات القانونية أو المتعلقة بالسلامة أو منع الاحتيال أو الأمان إفصاحاً محدوداً.",
        ],
      },
      {
        id: "choices-security",
        title: "خياراتك والأمان",
        paragraphs: [
          "يمكنك تحديث إعدادات الملف الشخصي واللغة والمنطقة الزمنية والوحدات والتفضيلات الغذائية وخيارات الإشعارات داخل التطبيق. عند توفرها، يمكنك طلب تصدير البيانات أو حذف الحساب.",
          "نستخدم ضوابط وصول، وتشفيراً أثناء النقل، وسجلات تدقيق، وإجراءات تشغيلية. لا يمكن ضمان أمان أي خدمة عبر الإنترنت بالكامل، ويجب التعامل مع الحالات الطبية العاجلة عبر مختصين مؤهلين أو خدمات الطوارئ.",
        ],
      },
      {
        id: "contact",
        title: "التواصل",
        paragraphs: ["لأسئلة الخصوصية أو طلبات البيانات أو مخاوف السلامة، تواصل عبر support@theproperhumandiet.net."],
      },
    ],
    terms: [
      {
        id: "acceptance",
        title: "قبول الشروط",
        paragraphs: [
          "باستخدام ProperHumanDiet أو الوصول إليها، فإنك توافق على هذه الشروط. إذا كنت لا توافق، فلا تستخدم الخدمة.",
          "يجب أن تكون مؤهلاً قانونياً لاستخدام الخدمة ومسؤولاً عن المعلومات التي تقدمها.",
        ],
      },
      {
        id: "service",
        title: "الخدمة",
        paragraphs: [
          "توفر ProperHumanDiet أدوات لتتبع البروتوكولات الغذائية، الأعراض، المكملات، الأدوية، المختبرات، عادات نمط الحياة، التقدم، مصادر المعرفة، دعم المجتمع، وتدفقات العمل المدعومة بالذكاء الاصطناعي المدفوع.",
        ],
      },
      {
        id: "medical-disclaimer",
        title: "إخلاء مسؤولية طبي",
        paragraphs: [
          "توفر ProperHumanDiet أدوات تعليمية وأدوات تتبع فقط. لا تشخص أو تعالج أو تشفي أو تمنع الأمراض، ولا تحل محل الطبيب أو تقدم رعاية طارئة.",
          "استشر دائماً مختصين صحيين مؤهلين قبل تغيير الأدوية أو المكملات أو النظام الغذائي أو خطط العلاج.",
        ],
      },
      {
        id: "accounts-content",
        title: "الحسابات ومحتوى المستخدم",
        paragraphs: [
          "أنت مسؤول عن حماية بيانات الدخول، إبقاء معلومات الحساب دقيقة، والتأكد من امتلاكك الحق في رفع أو مشاركة أي محتوى تقدمه.",
          "تحتفظ بملكية المحتوى الذي تقدمه. تمنح ProperHumanDiet الإذن اللازم لتخزينه وعرضه ومعالجته وترجمته وتلخيصه وتحليله ونقله فقط لتشغيل الخدمة والميزات التي تطلبها.",
        ],
      },
      {
        id: "credits-ai",
        title: "الأرصدة والمحفظة واستخدام الذكاء الاصطناعي",
        paragraphs: [
          "تستخدم ميزات الذكاء الاصطناعي المدفوعة حزم أرصدة لمرة واحدة وأرصدة المحفظة. يجب أن يعرض التطبيق تقدير تكلفة أو مؤشر أرصدة قبل المكالمات المدفوعة عند الاقتضاء.",
          "قد تخصم المكالمات الناجحة أرصدة وتنشئ سجلات استخدام. يجب أن تفشل أخطاء المزود بسلاسة وأن تُرد الأرصدة عند حدوث خصم دون نتيجة مكتملة. إذا كان الرصيد غير كافٍ، يجب شحن المحفظة قبل المتابعة.",
        ],
      },
      {
        id: "community",
        title: "سلوك المجتمع",
        paragraphs: [
          "استخدم ميزات المجتمع باحترام. لا تضايق الآخرين، ولا تنشر تعليمات طبية خطرة، ولا تنتحل الشخصيات، ولا ترسل رسائل مزعجة، ولا تنتهك الخصوصية، ولا ترفع محتوى لا تملك حق مشاركته.",
          "قد نقوم بمراجعة أو إزالة أو تقييد أو الإبلاغ عن المحتوى أو الحسابات التي تسبب مخاطر تتعلق بالسلامة أو القانون أو الخصوصية أو إساءة الاستخدام.",
        ],
      },
      {
        id: "contact",
        title: "التواصل",
        paragraphs: ["للأسئلة حول هذه الشروط، تواصل عبر support@theproperhumandiet.net."],
      },
    ],
  },
  de: {
    privacy: [
      {
        id: "introduction",
        title: "Einführung",
        paragraphs: [
          "ProperHumanDiet ist eine Gesundheits-Tracking-Plattform für Ernährungsprotokolle, Symptome, Lifestyle-Protokolle, Wissensinhalte, Community-Unterstützung und kostenpflichtige KI-Werkzeuge.",
          "Diese Richtlinie erklärt, wie wir Informationen verarbeiten, die Sie bei der Nutzung von Website, Dashboard, Community, Wallet und KI-gestützten Funktionen bereitstellen. ProperHumanDiet ist kein medizinischer Anbieter.",
        ],
      },
      {
        id: "data-we-collect",
        title: "Informationen, die wir erheben",
        paragraphs: ["Wir erheben nur Informationen, die zum Betrieb des Dienstes, zum Schutz von Konten, zur Personalisierung des Trackings und zur Bereitstellung angeforderter KI-Funktionen erforderlich sind."],
        items: [
          "Konto- und Profildaten wie Name, E-Mail, Sprache, Zeitzone, Land, Biografie und Präferenzen.",
          "Gesundheitseinträge wie Symptome, Essensprotokolle, Nahrungsergänzungen, Medikamente, Laborergebnisse, Arztbesuche, Fotos, Journale und Protokollfortschritt.",
          "Community-Inhalte wie Beiträge, Rezepte, Kommentare, Reaktionen, Meldungen, Erwähnungen und Nachrichten.",
          "Zahlungsdaten wie Wallet-Saldo, Kreditbuchungen, Checkout-Status, Rückerstattungen und Transaktionskennungen.",
          "Sicherheits- und Nutzungsdaten wie Geräteinformationen, Sitzungen, Anfrageprotokolle und Fehlerdiagnosen.",
        ],
      },
      {
        id: "ai-and-credits",
        title: "KI-Funktionen und Credits",
        paragraphs: [
          "Wenn Sie KI-Funktionen nutzen, verarbeiten wir Prompt, hochgeladene Datei, ausgewählte Sprache, relevanten Gesundheitskontext und Nutzungsmetadaten, die für Antwort, Kostenschätzung, Abbuchung oder Rückerstattung von Credits und Audit-Protokolle nötig sind.",
          "Funktionen können Gesundheitsassistent, Mahlzeitenfotoanalyse, Symptomfotoanalyse, Laborinterpretation, Arztbesuchszusammenfassungen, Transkriptverbesserung, Mahlzeitenplanung, Community-Personas und andere KI-Oberflächen umfassen. Über die System-Wallet geroutete Funktionen belasten Ihre persönliche Wallet nicht.",
        ],
      },
      {
        id: "sharing",
        title: "Weitergabe von Informationen",
        paragraphs: ["Wir verkaufen keine persönlichen Gesundheitsinformationen. Daten werden nur geteilt, wenn dies zur Leistungserbringung, Rechtsbefolgung, Missbrauchsvermeidung oder Durchführung einer von Ihnen gewünschten Aktion nötig ist."],
        items: [
          "Infrastruktur-, E-Mail-, Analyse-, Monitoring-, Zahlungs- und KI-Anbieter erhalten nur die Daten, die sie für ihre Rolle benötigen.",
          "Community-Inhalte können je nach Oberfläche für andere Nutzer sichtbar sein.",
          "Rechtliche, sicherheitsbezogene, Betrugspräventions- oder Sicherheitsanfragen können eine begrenzte Offenlegung erfordern.",
        ],
      },
      {
        id: "choices-security",
        title: "Ihre Wahlmöglichkeiten und Sicherheit",
        paragraphs: [
          "Sie können Profil, Sprache, Zeitzone, Einheiten, Ernährungspräferenzen und Benachrichtigungen in der App ändern. Soweit verfügbar, können Sie Exporte oder Kontolöschung anfordern.",
          "Wir verwenden Zugriffskontrollen, Verschlüsselung bei der Übertragung, Audit-Protokolle und betriebliche Schutzmaßnahmen. Kein Onlinedienst ist vollkommen sicher; dringende medizinische Probleme gehören zu qualifizierten Fachkräften oder Notdiensten.",
        ],
      },
      {
        id: "contact",
        title: "Kontakt",
        paragraphs: ["Bei Datenschutzfragen, Datenanfragen oder Sicherheitsbedenken kontaktieren Sie support@theproperhumandiet.net."],
      },
    ],
    terms: [
      {
        id: "acceptance",
        title: "Annahme der Bedingungen",
        paragraphs: [
          "Durch Zugriff auf oder Nutzung von ProperHumanDiet stimmen Sie diesen Bedingungen zu. Wenn Sie nicht einverstanden sind, nutzen Sie den Dienst nicht.",
          "Sie müssen rechtlich zur Nutzung des Dienstes berechtigt und für die von Ihnen übermittelten Informationen verantwortlich sein.",
        ],
      },
      {
        id: "service",
        title: "Der Dienst",
        paragraphs: ["ProperHumanDiet bietet Werkzeuge zum Tracking von Ernährungsprotokollen, Symptomen, Nahrungsergänzungen, Medikamenten, Laboren, Lifestyle-Gewohnheiten, Fortschritt, Wissensinhalten, Community-Unterstützung und kostenpflichtigen KI-Workflows."],
      },
      {
        id: "medical-disclaimer",
        title: "Medizinischer Hinweis",
        paragraphs: [
          "ProperHumanDiet bietet nur Bildungs- und Tracking-Werkzeuge. Es diagnostiziert, behandelt, heilt oder verhindert keine Krankheiten, ersetzt keine ärztliche Fachkraft und bietet keine Notfallversorgung.",
          "Konsultieren Sie immer qualifizierte Gesundheitsfachkräfte, bevor Sie Medikamente, Nahrungsergänzungen, Ernährung oder Behandlungspläne ändern.",
        ],
      },
      {
        id: "accounts-content",
        title: "Konten und Nutzerinhalte",
        paragraphs: [
          "Sie sind dafür verantwortlich, Ihre Zugangsdaten zu schützen, Kontoinformationen korrekt zu halten und nur Inhalte hochzuladen oder zu teilen, zu denen Sie berechtigt sind.",
          "Sie behalten das Eigentum an Ihren Inhalten. Sie erteilen ProperHumanDiet die Erlaubnis, diese nur zum Betrieb des Dienstes und der von Ihnen angeforderten Funktionen zu speichern, anzuzeigen, zu verarbeiten, zu übersetzen, zusammenzufassen, zu analysieren und zu übertragen.",
        ],
      },
      {
        id: "credits-ai",
        title: "Credits, Wallet und KI-Nutzung",
        paragraphs: [
          "Kostenpflichtige KI-Funktionen nutzen einmalige Credit-Pakete und Wallet-Salden. Die App sollte, wo anwendbar, vor kostenpflichtigen Aufrufen eine Kostenschätzung oder Credit-Anzeige zeigen.",
          "Erfolgreiche KI-Aufrufe können Credits abbuchen und Nutzungsprotokolle erstellen. Anbieterfehler sollten sauber fehlschlagen und Credits erstatten, wenn eine Abbuchung ohne abgeschlossenes Ergebnis erfolgte. Bei unzureichendem Guthaben ist vor der Fortsetzung eine Aufladung erforderlich.",
        ],
      },
      {
        id: "community",
        title: "Community-Verhalten",
        paragraphs: [
          "Nutzen Sie Community-Funktionen respektvoll. Belästigung, gefährliche medizinische Anweisungen, Identitätsmissbrauch, Spam, Datenschutzverletzungen oder Inhalte ohne Nutzungsrecht sind nicht erlaubt.",
          "Wir können Inhalte oder Konten moderieren, entfernen, einschränken oder melden, wenn Risiken für Sicherheit, Recht, Datenschutz oder Missbrauch entstehen.",
        ],
      },
      {
        id: "contact",
        title: "Kontakt",
        paragraphs: ["Bei Fragen zu diesen Bedingungen kontaktieren Sie support@theproperhumandiet.net."],
      },
    ],
  },
  es: {
    privacy: [
      {
        id: "introduction",
        title: "Introducción",
        paragraphs: [
          "ProperHumanDiet es una plataforma de seguimiento de salud para protocolos dietarios, síntomas, registros de estilo de vida, recursos de conocimiento, apoyo comunitario y herramientas de IA de pago.",
          "Esta política explica cómo tratamos la información que proporcionas al usar el sitio web, el panel, la comunidad, la billetera y las funciones asistidas por IA. ProperHumanDiet no es un proveedor médico.",
        ],
      },
      {
        id: "data-we-collect",
        title: "Información que recopilamos",
        paragraphs: ["Recopilamos solo la información necesaria para operar el servicio, proteger cuentas, personalizar el seguimiento y ofrecer las funciones de IA solicitadas."],
        items: [
          "Datos de cuenta y perfil como nombre, correo, idioma, zona horaria, país, biografía y preferencias.",
          "Registros de salud como síntomas, comidas, suplementos, medicamentos, laboratorios, visitas, fotos, diarios y progreso de protocolos.",
          "Contenido comunitario como publicaciones, recetas, comentarios, reacciones, reportes, menciones y mensajes.",
          "Registros de pago como saldo de billetera, historial de créditos, estado de checkout, reembolsos e identificadores de transacción.",
          "Datos de seguridad y uso como metadatos del dispositivo, sesiones, registros de solicitudes y diagnósticos de errores.",
        ],
      },
      {
        id: "ai-and-credits",
        title: "Funciones de IA y créditos",
        paragraphs: [
          "Cuando usas funciones de IA, procesamos el prompt, archivo cargado, idioma elegido, contexto de salud relevante y metadatos de uso necesarios para responder, estimar costes, debitar o reembolsar créditos y crear registros de auditoría.",
          "Las funciones pueden incluir asistente de salud, análisis de fotos de comidas y síntomas, interpretación de laboratorio, resúmenes de visitas, mejora de transcripciones, planificación de comidas, personas comunitarias y otras superficies de IA. Las funciones enrutadas por la billetera del sistema no debitan tu billetera personal.",
        ],
      },
      {
        id: "sharing",
        title: "Cómo se comparte la información",
        paragraphs: ["No vendemos información personal de salud. Compartimos datos solo cuando es necesario para prestar el servicio, cumplir la ley, prevenir abusos o ejecutar una acción solicitada por el usuario."],
        items: [
          "Proveedores de infraestructura, correo, analítica, monitoreo, pagos e IA reciben solo los datos necesarios para su función.",
          "El contenido comunitario puede ser visible para otros usuarios según la superficie donde lo publiques.",
          "Solicitudes legales, de seguridad, prevención de fraude o protección pueden requerir divulgación limitada.",
        ],
      },
      {
        id: "choices-security",
        title: "Tus opciones y seguridad",
        paragraphs: [
          "Puedes actualizar perfil, idioma, zona horaria, unidades, preferencias dietarias y notificaciones en la app. Cuando esté disponible, puedes solicitar exportaciones o eliminación de cuenta.",
          "Usamos controles de acceso, cifrado en tránsito, registros de auditoría y salvaguardas operativas. Ningún servicio en línea puede garantizar seguridad perfecta, y los problemas médicos urgentes deben tratarse con profesionales cualificados o servicios de emergencia.",
        ],
      },
      {
        id: "contact",
        title: "Contacto",
        paragraphs: ["Para preguntas de privacidad, solicitudes de datos o inquietudes de seguridad, contacta con support@theproperhumandiet.net."],
      },
    ],
    terms: [
      {
        id: "acceptance",
        title: "Aceptación de los términos",
        paragraphs: [
          "Al acceder o usar ProperHumanDiet, aceptas estos Términos. Si no estás de acuerdo, no uses el servicio.",
          "Debes tener capacidad legal para usar el servicio y ser responsable de la información que envías.",
        ],
      },
      {
        id: "service",
        title: "El servicio",
        paragraphs: ["ProperHumanDiet ofrece herramientas para seguir protocolos dietarios, síntomas, suplementos, medicamentos, laboratorios, hábitos de estilo de vida, progreso, conocimiento, comunidad y flujos de trabajo de IA de pago."],
      },
      {
        id: "medical-disclaimer",
        title: "Aviso médico",
        paragraphs: [
          "ProperHumanDiet ofrece solo herramientas educativas y de seguimiento. No diagnostica, trata, cura ni previene enfermedades, no sustituye a un profesional clínico y no ofrece atención de emergencia.",
          "Consulta siempre con profesionales sanitarios cualificados antes de cambiar medicamentos, suplementos, dieta o planes de tratamiento.",
        ],
      },
      {
        id: "accounts-content",
        title: "Cuentas y contenido del usuario",
        paragraphs: [
          "Eres responsable de proteger tus credenciales, mantener la información de la cuenta actualizada y asegurarte de tener derecho a cargar o compartir cualquier contenido que envíes.",
          "Conservas la propiedad de tu contenido. Otorgas a ProperHumanDiet el permiso necesario para almacenarlo, mostrarlo, procesarlo, traducirlo, resumirlo, analizarlo y transmitirlo solo para operar el servicio y las funciones que solicites.",
        ],
      },
      {
        id: "credits-ai",
        title: "Créditos, billetera y uso de IA",
        paragraphs: [
          "Las funciones de IA de pago usan paquetes de créditos de una sola compra y saldos de billetera. La app debe mostrar una estimación de coste o indicador de créditos antes de llamadas pagas cuando corresponda.",
          "Las llamadas de IA exitosas pueden debitar créditos y crear registros de uso. Los fallos del proveedor deben manejarse sin bloqueo y reembolsar créditos si hubo débito sin resultado completo. El saldo insuficiente requiere recargar antes de continuar.",
        ],
      },
      {
        id: "community",
        title: "Conducta comunitaria",
        paragraphs: [
          "Usa las funciones comunitarias con respeto. No acoses, no publiques instrucciones médicas peligrosas, no suplantes identidades, no hagas spam, no violes privacidad ni subas contenido sin permiso.",
          "Podemos moderar, eliminar, restringir o reportar contenido o cuentas que generen riesgos de seguridad, legales, privacidad o abuso.",
        ],
      },
      {
        id: "contact",
        title: "Contacto",
        paragraphs: ["Para preguntas sobre estos Términos, contacta con support@theproperhumandiet.net."],
      },
    ],
  },
  fr: {
    privacy: [
      {
        id: "introduction",
        title: "Introduction",
        paragraphs: [
          "ProperHumanDiet est une plateforme de suivi santé pour les protocoles alimentaires, les symptômes, les journaux de mode de vie, les ressources de connaissance, le soutien communautaire et les outils d’IA payants.",
          "Cette politique explique comment nous traitons les informations fournies lors de l’utilisation du site, du tableau de bord, de la communauté, du portefeuille et des fonctions assistées par IA. ProperHumanDiet n’est pas un prestataire médical.",
        ],
      },
      {
        id: "data-we-collect",
        title: "Informations collectées",
        paragraphs: ["Nous collectons uniquement les informations nécessaires pour exploiter le service, protéger les comptes, personnaliser le suivi et fournir les fonctions d’IA demandées."],
        items: [
          "Données de compte et de profil comme le nom, l’e-mail, la langue, le fuseau horaire, le pays, la biographie et les préférences.",
          "Entrées de suivi santé comme symptômes, repas, compléments, médicaments, laboratoires, visites, photos, journaux et progression des protocoles.",
          "Contenu communautaire comme publications, recettes, commentaires, réactions, signalements, mentions et messages.",
          "Données de paiement comme solde du portefeuille, historique des crédits, état du paiement, remboursements et identifiants de transaction.",
          "Données de sécurité et d’usage comme métadonnées d’appareil, sessions, journaux de requêtes et diagnostics d’erreurs.",
        ],
      },
      {
        id: "ai-and-credits",
        title: "Fonctions IA et crédits",
        paragraphs: [
          "Lorsque vous utilisez des fonctions d’IA, nous traitons la consigne, le fichier téléversé, la langue choisie, le contexte santé pertinent et les métadonnées d’usage nécessaires pour répondre, estimer le coût, débiter ou rembourser des crédits et créer des journaux d’audit.",
          "Les fonctions peuvent inclure l’assistant santé, l’analyse de photos de repas ou de symptômes, l’interprétation de laboratoire, les résumés de visites, l’amélioration de transcriptions, la planification de repas, les personas communautaires et d’autres surfaces IA. Les fonctions routées par le portefeuille système ne débitent pas votre portefeuille personnel.",
        ],
      },
      {
        id: "sharing",
        title: "Partage des informations",
        paragraphs: ["Nous ne vendons pas les informations personnelles de santé. Nous partageons les données uniquement lorsque c’est nécessaire pour fournir le service, respecter la loi, prévenir les abus ou exécuter une action demandée par l’utilisateur."],
        items: [
          "Les fournisseurs d’infrastructure, d’e-mail, d’analyse, de surveillance, de paiement et d’IA reçoivent seulement les données nécessaires à leur rôle.",
          "Le contenu communautaire peut être visible par d’autres utilisateurs selon l’espace où vous le publiez.",
          "Les demandes légales, de sécurité, de prévention de fraude ou de protection peuvent nécessiter une divulgation limitée.",
        ],
      },
      {
        id: "choices-security",
        title: "Vos choix et la sécurité",
        paragraphs: [
          "Vous pouvez modifier le profil, la langue, le fuseau horaire, les unités, les préférences alimentaires et les notifications dans l’application. Lorsque disponible, vous pouvez demander une exportation ou une suppression de compte.",
          "Nous utilisons des contrôles d’accès, le chiffrement en transit, des journaux d’audit et des protections opérationnelles. Aucun service en ligne n’est parfaitement sécurisé ; les urgences médicales doivent être traitées par des professionnels qualifiés ou les services d’urgence.",
        ],
      },
      {
        id: "contact",
        title: "Contact",
        paragraphs: ["Pour les questions de confidentialité, les demandes de données ou les préoccupations de sécurité, contactez support@theproperhumandiet.net."],
      },
    ],
    terms: [
      {
        id: "acceptance",
        title: "Acceptation des conditions",
        paragraphs: [
          "En accédant à ProperHumanDiet ou en l’utilisant, vous acceptez ces Conditions. Si vous n’êtes pas d’accord, n’utilisez pas le service.",
          "Vous devez être juridiquement apte à utiliser le service et responsable des informations que vous soumettez.",
        ],
      },
      {
        id: "service",
        title: "Le service",
        paragraphs: ["ProperHumanDiet fournit des outils pour suivre protocoles alimentaires, symptômes, compléments, médicaments, laboratoires, habitudes de vie, progression, ressources de connaissance, communauté et workflows d’IA payants."],
      },
      {
        id: "medical-disclaimer",
        title: "Avertissement médical",
        paragraphs: [
          "ProperHumanDiet fournit uniquement des outils éducatifs et de suivi. Il ne diagnostique pas, ne traite pas, ne guérit pas et ne prévient pas les maladies, ne remplace pas un clinicien et ne fournit pas de soins d’urgence.",
          "Consultez toujours des professionnels de santé qualifiés avant de modifier médicaments, compléments, régime alimentaire ou plans de traitement.",
        ],
      },
      {
        id: "accounts-content",
        title: "Comptes et contenu utilisateur",
        paragraphs: [
          "Vous êtes responsable de la protection de vos identifiants, de l’exactitude des informations de compte et du droit de téléverser ou partager tout contenu soumis.",
          "Vous conservez la propriété de votre contenu. Vous accordez à ProperHumanDiet l’autorisation nécessaire pour le stocker, l’afficher, le traiter, le traduire, le résumer, l’analyser et le transmettre uniquement pour exploiter le service et les fonctions demandées.",
        ],
      },
      {
        id: "credits-ai",
        title: "Crédits, portefeuille et IA",
        paragraphs: [
          "Les fonctions d’IA payantes utilisent des packs de crédits ponctuels et le solde du portefeuille. L’application doit afficher une estimation du coût ou un indicateur de crédits avant les appels payants lorsque c’est applicable.",
          "Les appels IA réussis peuvent débiter des crédits et créer des journaux d’usage. Les erreurs fournisseur doivent échouer proprement et rembourser les crédits lorsqu’un débit a eu lieu sans résultat complet. Un solde insuffisant nécessite une recharge avant de continuer.",
        ],
      },
      {
        id: "community",
        title: "Conduite communautaire",
        paragraphs: [
          "Utilisez les fonctions communautaires avec respect. Ne harcelez pas, ne publiez pas d’instructions médicales dangereuses, n’usurpez pas d’identité, ne faites pas de spam, ne violez pas la vie privée et ne téléversez pas de contenu sans autorisation.",
          "Nous pouvons modérer, supprimer, restreindre ou signaler des contenus ou comptes créant des risques de sécurité, juridiques, de confidentialité ou d’abus.",
        ],
      },
      {
        id: "contact",
        title: "Contact",
        paragraphs: ["Pour toute question sur ces Conditions, contactez support@theproperhumandiet.net."],
      },
    ],
  },
};

function resolveLegalLanguage(language: string): LegalLanguage {
  const normalized = language.split("-")[0] as LegalLanguage;
  return SUPPORTED_LEGAL_LANGUAGES.includes(normalized) ? normalized : "en";
}

const PAYMENT_LEGAL_SECTION_IDS = new Set(["ai-and-credits", "credits-ai"]);
const PAYMENT_LEGAL_TEXT_PATTERN =
  /payment|wallet|credit|checkout|refund|transaction|stripe|billing|paid ai|paid AI|crédit|crédito|cartera|portefeuille|remboursement|reembolso|zahlung|erstattung|guthaben|محفظ|رصيد|ائتمان|دفع|مدفوعة/i;

function filterPaymentLegalCopy(sections: LegalSectionCopy[]): LegalSectionCopy[] {
  return sections
    .filter((section) => !PAYMENT_LEGAL_SECTION_IDS.has(section.id))
    .map((section) => ({
      ...section,
      paragraphs: section.paragraphs.filter(
        (paragraph) => !PAYMENT_LEGAL_TEXT_PATTERN.test(paragraph),
      ),
      items: section.items?.filter((item) => !PAYMENT_LEGAL_TEXT_PATTERN.test(item)),
    }))
    .filter((section) => section.paragraphs.length > 0 || (section.items?.length ?? 0) > 0);
}

function ProductLegalSections({
  sections,
  includePaymentContent,
}: {
  sections: LegalSectionCopy[];
  includePaymentContent: boolean;
}) {
  const visibleSections = includePaymentContent
    ? sections
    : filterPaymentLegalCopy(sections);

  return (
    <div className="space-y-8">
      {visibleSections.map((section) => (
        <section key={section.id} id={section.id} className="space-y-3">
          <h2 className="text-xl font-semibold tracking-tight text-foreground">
            {section.title}
          </h2>
          {section.paragraphs.map((paragraph) => (
            <p key={paragraph} className="leading-7 text-muted-foreground">
              {paragraph}
            </p>
          ))}
          {section.items ? (
            <ul className="list-disc space-y-2 ps-6 text-muted-foreground">
              {section.items.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          ) : null}
        </section>
      ))}
    </div>
  );
}

function ProductLegalPageLayout({
  title,
  lastUpdated,
  effectiveDate,
  printLabel,
  children,
}: {
  title: string;
  lastUpdated: string;
  effectiveDate: string;
  printLabel: string;
  children: ReactNode;
}) {
  return (
    <main className="min-h-screen bg-background text-foreground">
      <div className="container mx-auto max-w-4xl px-4 py-10 sm:px-6 lg:px-8">
        <header className="mb-8 space-y-4 border-b pb-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="space-y-2">
              <h1 className="text-3xl font-bold tracking-tight">{title}</h1>
              <div className="space-y-1 text-sm text-muted-foreground">
                <p>{lastUpdated}</p>
                <p>{effectiveDate}</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => window.print()}
              className="rounded-md border border-input bg-background px-4 py-2 text-sm font-medium shadow-sm transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {printLabel}
            </button>
          </div>
        </header>
        <article className="prose prose-neutral max-w-none dark:prose-invert">
          {children}
        </article>
      </div>
    </main>
  );
}

export function ProductTermsPage() {
  const { t, i18n } = useTranslation("legal");
  const { enabled: paymentsEnabled } = usePaywall();
  const legalLanguage = resolveLegalLanguage(i18n.language);

  return (
    <ProductLegalPageLayout
      title={t("terms.title")}
      lastUpdated={`${t("common.lastUpdated")}: ${t("terms.lastUpdated")}`}
      effectiveDate={`${t("common.effectiveDate")}: ${t("terms.effectiveDate")}`}
      printLabel={t("common.printPage")}
    >
      <ProductLegalSections
        sections={LEGAL_COPY[legalLanguage].terms}
        includePaymentContent={paymentsEnabled}
      />
    </ProductLegalPageLayout>
  );
}

export function ProductPrivacyPage() {
  const { t, i18n } = useTranslation("legal");
  const { enabled: paymentsEnabled } = usePaywall();
  const legalLanguage = resolveLegalLanguage(i18n.language);

  return (
    <ProductLegalPageLayout
      title={t("privacy.title")}
      lastUpdated={`${t("common.lastUpdated")}: ${t("privacy.lastUpdated")}`}
      effectiveDate={`${t("common.effectiveDate")}: ${t("privacy.effectiveDate")}`}
      printLabel={t("common.printPage")}
    >
      <ProductLegalSections
        sections={LEGAL_COPY[legalLanguage].privacy}
        includePaymentContent={paymentsEnabled}
      />
    </ProductLegalPageLayout>
  );
}

export function ProductMedicalDisclaimerPage() {
  const { t, i18n } = useTranslation("legal");
  const legalLanguage = resolveLegalLanguage(i18n.language);

  const disclaimerSection = LEGAL_COPY[legalLanguage].terms.find(
    (s) => s.id === "medical-disclaimer",
  );
  const sections = disclaimerSection ? [disclaimerSection] : [];

  return (
    <ProductLegalPageLayout
      title={t("medicalDisclaimer.title")}
      lastUpdated=""
      effectiveDate=""
      printLabel={t("common.printPage")}
    >
      <ProductLegalSections sections={sections} includePaymentContent={false} />
    </ProductLegalPageLayout>
  );
}
